import { useState, useEffect, ChangeEvent } from 'react';
import EastIcon from '@mui/icons-material/East';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ConfirmDialog from './ConfirmDialog';
import OutlinedTextField from './fields/OutlinedTextField';
import { OfferingData } from './utilities/PlanConfigurationUtils';
import PlanConfigurationUtils from './utilities/PlanConfigurationUtils';
import StandardTable, { makeHeader, makeRow } from './StandardTable';

const BoldText = styled('span')({ fontWeight: 600 });

interface PlanTransitionsDialogProps {
  open: boolean;
  planYear: string;
  onClose: () => void;
  employerId?: string;
  portal: string;
}

const { useSuccessorOfferings } = PlanConfigurationUtils;

function PlanTransitionsDialog({
  open,
  planYear,
  onClose,
  employerId,
  portal,
}: PlanTransitionsDialogProps) {
  const [successors, setSuccessors] = useState<OfferingData[]>([]);

  function handleSuccessorChange(index: number) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      setSuccessors((values) => {
        return values.map((item, i) => {
          if (i === index) {
            return {
              id: item.id,
              successor_offering_id: event.target.value,
            };
          }
          return item;
        });
      });
    };
  }

  const onSave = () => {
    updateSuccessors({ plan_year_id: planYear, successors });
    onClose();
  };

  const { successorOfferings, updateSuccessors } = useSuccessorOfferings({
    portal,
    employerId,
    planYearId: planYear,
  });

  useEffect(() => {
    const newSuccessors = (successorOfferings.last_year_offerings || []).map((offering: any) => {
      return { id: offering.id, successor_offering_id: offering.successor_offering_id };
    });
    if (open) {
      setSuccessors(newSuccessors);
    }
  }, [successorOfferings, open]);

  const planTransitionsValid = () => {
    return successors.every((value) => !!value.successor_offering_id);
  };

  const successorItemsList = () => {
    return (successorOfferings.this_year_offerings || []).map((offering: OfferingData) => {
      return (
        <MenuItem key={`plan-type-${offering.id}`} value={offering.id}>
          {offering.plan_name}
        </MenuItem>
      );
    });
  };
  const transitionsHeaders = makeHeader('transitions-table', [
    'During open enrollment',
    '',
    "Next year's plan",
  ]);

  const transitionsRows = (successorOfferings.last_year_offerings || []).map(
    (offering: OfferingData, index: number) =>
      makeRow(`transition-${index}`, [
        <Typography>
          Members on <BoldText>{offering.plan_name}</BoldText> will transition to
        </Typography>,
        <EastIcon fontSize="small" />,
        <OutlinedTextField
          select
          id="offering-transition"
          name="offeringTransition"
          label=""
          onChange={(event: ChangeEvent<HTMLInputElement>) => handleSuccessorChange(index)(event)}
          value={successors[index]?.successor_offering_id}
        >
          {successorItemsList()}
        </OutlinedTextField>,
      ])
  );

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      dialogName="plan-transitions-dialog"
      fullWidth={false}
      maxWidth="lg"
      title="Plan re-enrollment options"
      disableSubmit={!planTransitionsValid()}
      confirmText="Save"
      onConfirm={onSave}
      onCancel={onClose}
    >
      <Stack>
        <Typography>
          During open enrollment, members that are currently enrolled in a plan are automatically
          enrolled in next year's plan. Please verify the transitions from the current plan to the
          next year's plan.
        </Typography>
      </Stack>
      <Stack sx={{ marginTop: 6, marginBottom: 2 }}>
        <StandardTable
          id="linked-reimbursements"
          headers={transitionsHeaders}
          rows={transitionsRows}
          noDataMessage="No transitions related to this plan year."
        />
      </Stack>
    </ConfirmDialog>
  );
}

export default PlanTransitionsDialog;
